import { Wallet as WalletIcon } from "lucide-react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { MoneyText } from "@/components/ui/MoneyText";
import type { Member, Transaction } from "@/lib/schemas";

type Props = {
  transactions: Transaction[];
  adults: Member[];
};

/**
 * Per-adult agregacja zarobków dzieci w tym tygodniu, pogrupowane
 * po `transaction.fundedBy`. Dla parent dashboardu — żeby od razu
 * widzieć kto ile zafundował.
 */
export function WeeklyFunderBreakdown({ transactions, adults }: Props) {
  // Map<funderUid, sumMinor>
  const sums = new Map<string, number>();
  for (const t of transactions) {
    const funder = t.fundedBy ?? "__unknown";
    sums.set(funder, (sums.get(funder) ?? 0) + t.amountMinor);
  }
  const total = Array.from(sums.values()).reduce((s, v) => s + v, 0);

  if (total === 0) {
    return (
      <Card>
        <CardHeader className="flex items-center gap-2">
          <WalletIcon className="h-3.5 w-3.5 text-muted" />
          <CardTitle>Ten tydzień</CardTitle>
        </CardHeader>
        <CardBody className="text-sm text-muted">
          Brak zatwierdzonych zarobków w tym tygodniu.
        </CardBody>
      </Card>
    );
  }

  // Sort by sum desc, push unknowns last
  const rows = adults
    .map((a) => ({
      uid: a.userId,
      name: a.displayName,
      relation: a.relation,
      sum: sums.get(a.userId) ?? 0,
    }))
    .filter((r) => r.sum > 0)
    .sort((a, b) => b.sum - a.sum);

  // Wpadki bez fundedBy lub funder spoza listy adults
  const otherSum = total - rows.reduce((s, r) => s + r.sum, 0);

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <WalletIcon className="h-3.5 w-3.5 text-muted" />
        <CardTitle>Kto ile zafundował (ten tydzień)</CardTitle>
      </CardHeader>
      <ul className="divide-y divide-line">
        {rows.map((r) => {
          const pct = total > 0 ? Math.round((r.sum / total) * 100) : 0;
          return (
            <li key={r.uid} className="px-4 py-3 flex items-center gap-3">
              <div className="h-8 w-8 rounded-[10px] bg-soft flex items-center justify-center text-sm font-semibold">
                {r.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {r.relation || r.name}
                </p>
                <div className="mt-1 h-1 rounded-full bg-soft overflow-hidden">
                  <div
                    className="h-full bg-money"
                    style={{ width: `${pct}%` }}
                    aria-hidden
                  />
                </div>
              </div>
              <div className="text-right">
                <MoneyText
                  minor={r.sum}
                  tone="money"
                  className="text-sm font-semibold"
                />
                <div className="text-[10px] text-muted font-num">{pct}%</div>
              </div>
            </li>
          );
        })}
        {otherSum > 0 && (
          <li className="px-4 py-3 flex items-center gap-3 text-muted">
            <div className="h-8 w-8 rounded-[10px] bg-soft flex items-center justify-center text-sm">
              ?
            </div>
            <div className="flex-1 text-sm">Inne / nieprzypisane</div>
            <MoneyText minor={otherSum} tone="ink" className="text-sm" />
          </li>
        )}
        <li className="px-4 py-3 flex items-center gap-3 bg-soft/50">
          <div className="h-8 w-8 rounded-[10px] flex items-center justify-center" />
          <div className="flex-1 text-sm font-semibold">Łącznie</div>
          <MoneyText minor={total} tone="money" className="text-base font-semibold" />
        </li>
      </ul>
    </Card>
  );
}
